import { useCallback, useEffect, useMemo, useState } from "react";
import { adminFetch } from "utils/adminFetch";

function CountryRow({ row, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(row.country)}
      className="flex w-full items-center justify-between gap-4 border-b border-gray-100 px-5 py-4 text-left transition hover:bg-brand-500/5 dark:border-white/5 dark:hover:bg-brand-400/10 sm:px-6"
    >
      <div>
        <span className="text-base font-bold text-navy-700 dark:text-white">{row.country}</span>
        {row.filleuls > 0 ? (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {row.filleuls} filleul{row.filleuls !== 1 ? "s" : ""} total
          </p>
        ) : null}
      </div>
      <span className="text-lg font-bold tabular-nums text-navy-700 dark:text-white">
        <span className="text-gray-400 dark:text-gray-500">|</span>{" "}
        <span className="text-emerald-600 dark:text-emerald-400">{row.parrains}</span>
      </span>
    </button>
  );
}

function LeaderboardView({ country, rows, loading, error, onBack, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="mb-2 text-sm font-semibold text-brand-500 hover:text-brand-600 dark:text-brand-300"
          >
            ← All countries
          </button>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            {country} — leaderboard
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Ranked by validated referrals (highest first)
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-60"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        {loading ? (
          <p className="px-5 py-12 text-center text-sm text-gray-500 dark:text-gray-400 sm:px-6">
            Loading leaderboard…
          </p>
        ) : error ? (
          <p className="px-5 py-12 text-center text-sm text-red-600 dark:text-red-400 sm:px-6">
            {error}
          </p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-gray-500 dark:text-gray-400 sm:px-6">
            No referrers for this country yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80 text-xs uppercase tracking-wide text-gray-500 dark:border-white/10 dark:bg-navy-900/50 dark:text-gray-400">
                  <th className="px-5 py-3 font-semibold sm:px-6">Rank</th>
                  <th className="px-5 py-3 font-semibold sm:px-6">Country</th>
                  <th className="px-5 py-3 font-semibold sm:px-6">ClientID</th>
                  <th className="px-5 py-3 font-semibold sm:px-6">Referrals</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const rank = row.rank != null ? row.rank : index + 1;
                  return (
                    <tr
                      key={`${row.clientId}-${rank}`}
                      className="border-b border-gray-50 last:border-0 dark:border-white/5"
                    >
                      <td className="px-5 py-3.5 font-semibold tabular-nums text-navy-800 dark:text-white sm:px-6">
                        #{rank}
                      </td>
                      <td className="px-5 py-3.5 text-gray-700 dark:text-gray-200 sm:px-6">
                        {row.country || country}
                      </td>
                      <td className="px-5 py-3.5 font-semibold tabular-nums text-navy-800 dark:text-white sm:px-6">
                        {row.clientId}
                      </td>
                      <td className="px-5 py-3.5 font-bold tabular-nums text-emerald-600 dark:text-emerald-400 sm:px-6">
                        {Number(row.referrals) || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminReferralContest() {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [error, setError] = useState("");

  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/contest/countries");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not load countries.");
        setCountries([]);
        return;
      }
      setCountries(Array.isArray(data.countries) ? data.countries : []);
    } catch {
      setError("Network error.");
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const loadLeaderboard = useCallback(async (country) => {
    if (!country) return;
    setLoadingLeaderboard(true);
    setError("");
    try {
      const res = await adminFetch(
        `/api/admin/contest/leaderboard?country=${encodeURIComponent(country)}`,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not load leaderboard.");
        setLeaderboardRows([]);
        return;
      }
      setLeaderboardRows(Array.isArray(data.rows) ? data.rows : []);
    } catch {
      setError("Network error.");
      setLeaderboardRows([]);
    } finally {
      setLoadingLeaderboard(false);
    }
  }, []);

  useEffect(() => {
    void loadCountries();
  }, [loadCountries]);

  useEffect(() => {
    if (!selectedCountry) return;
    void loadLeaderboard(selectedCountry);
  }, [selectedCountry, loadLeaderboard]);

  const totals = useMemo(() => {
    const parrains = countries.reduce((sum, row) => sum + (Number(row.parrains) || 0), 0);
    const filleuls = countries.reduce((sum, row) => sum + (Number(row.filleuls) || 0), 0);
    return { countries: countries.length, parrains, filleuls };
  }, [countries]);

  if (selectedCountry) {
    return (
      <LeaderboardView
        country={selectedCountry}
        rows={leaderboardRows}
        loading={loadingLeaderboard}
        error={error}
        onBack={() => {
          setSelectedCountry(null);
          setLeaderboardRows([]);
          setError("");
        }}
        onRefresh={() => void loadLeaderboard(selectedCountry)}
      />
    );
  }

  return (
    <div className="mt-3 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Referral contest
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Countries with referrers — click a row to open the country leaderboard.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadCountries()}
          disabled={loadingCountries}
          className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-60"
        >
          {loadingCountries ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-[20px] border border-gray-200 bg-white px-5 py-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Countries</p>
          <p className="mt-1 text-3xl font-bold text-navy-700 dark:text-white">
            {loadingCountries ? "…" : totals.countries}
          </p>
        </div>
        <div className="rounded-[20px] border border-gray-200 bg-white px-5 py-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Parrains</p>
          <p className="mt-1 text-3xl font-bold text-navy-700 dark:text-white">
            {loadingCountries ? "…" : totals.parrains}
          </p>
        </div>
        <div className="rounded-[20px] border border-gray-200 bg-white px-5 py-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Filleuls</p>
          <p className="mt-1 text-3xl font-bold text-navy-700 dark:text-white">
            {loadingCountries ? "…" : totals.filleuls}
          </p>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="border-b border-gray-100 px-5 py-4 dark:border-white/10 sm:px-6">
          <h2 className="text-lg font-bold text-navy-700 dark:text-white">By country</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Example: Algeria | 50 → 50 referrers in Algeria
          </p>
        </div>

        {loadingCountries ? (
          <p className="px-5 py-12 text-center text-sm text-gray-500 dark:text-gray-400 sm:px-6">
            Loading countries…
          </p>
        ) : countries.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-gray-500 dark:text-gray-400 sm:px-6">
            No referral data yet. Referrals appear after signups with{" "}
            <code className="rounded bg-gray-100 px-1 dark:bg-white/10">?ref=ClientID</code>.
          </p>
        ) : (
          <div>
            {countries.map((row) => (
              <CountryRow key={row.country} row={row} onSelect={setSelectedCountry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
